import { useEffect, useState, useRef } from "react";
import { ColorUtil } from "../../utils/color";
import { AlertService } from "../../services/Alert";
import { useCurrentAccount } from '../../hooks/useCurrentAccount';
import { useTranslation } from "react-i18next";
import { DashBoardService } from "../../services/DashBoardService";
import { NumberUtil } from "../../utils/number";
import { useSession } from "../../hooks/useSession";
import Aside from "../../components/Aside";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import useRequest from '../../hooks/useRequest';
import SemiDonutChart from "../../components/SemiDonutChart";
import CustomDataTable from "../../components/CustomDataTable";
import PieChart from "../../components/PieChart"
import { use } from "i18next";

export default function DashboardPage() {
    const { t } = useTranslation();
    const { account } = useCurrentAccount();
    const { call } = useRequest();
    const { session } = useSession();
    const [rows, setRows] = useState([])
    const [lastUpdate, setLastUpdate] = useState('yyyy-mm-dd 00:00:00')
    const [activeTab, setActiveTab] = useState('tabOne');
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const selectedFilterRef = useRef<string>('general');
    const [selectedFilterState, setSelectedFilterState] = useState('general');
    const [filterOptions, setFilterOptions] = useState([
        { value: 'general', label: t('dashboard-operations.general') },
        { value: 'agents', label: t('dashboard-operations.agents') },
        { value: 'bots', label: t('dashboard-operations.bot') }
    ]);
    const lockedSubAccountRef = useRef<string | null>(null);
    const accountId = session?.accounts?.[0]?.id;
    const userId = session?.id;
    const userFull = [4184, 5062];
    const shouldLockFilter = accountId === 512 && !userFull.includes(userId);

    //Agrega dinamicamente las subcuentas al filtro del dashboard
    function addOptionToFilter(label: string, value: string) {
        label = `${label}`
        setFilterOptions(prevOptions => [
            ...prevOptions,
            { label, value }
        ]);
    }

    //Guarda el listado de subcuentas, para no agregar repetidas al formulario
    const sub_accounts_map = useRef(new Map());

    const [cardsConversations, setCardsConversations] = useState({
        today: 0,
        yesterday: 0,
        total: 0,
        outAssign: 0,
        outResponseAgent: 0,
        outResponseClient: 0,
        slaMax: "00:00",
        slaMin: "00:00",
        slaAvg: "00:00"
    });

    const [cardsAgents, setCardsAgents] = useState({
        total: 0,
        conected: 0,
        break: 0,
        asign: 0,
        noasign: 0,
        chart: {
            labels: [],
            data: []
        }
    });

    const [tipificationsToday, setTipificationsToday] = useState<{ labels: string[], data: number[] }>({
        labels: [],
        data: []
    });

    const [tipificationsMonth, setTipificationsMonth] = useState<{ labels: string[], data: number[] }>({
        labels: [],
        data: []
    });

    const [totalAcumulatedToday, setTotalAcumulatedToday] = useState<number>(0);
    const [totalAcumulatedMonth, setTotalAcumulatedMonth] = useState<number>(0);
    const currentData = activeTab === 'tabOne' ? tipificationsToday : tipificationsMonth;
    const currentTotalAcumulated = activeTab === 'tabOne' ? totalAcumulatedToday : totalAcumulatedMonth;

    const columns = [
        {
            name: t('dashboard-operations.title-agents'),
            sortable: false,
            width: 20,
            center: true,
            selector: (row: { username: string; }) => row.username,
            cell: (row: any) => <p className="text-nowrap mb-0">{row.username || <span className="badge bg-soft-danger text-danger">{t('dashboard-operations.empty')}</span>}</p>
        },
        {
            name: 'Status',
            sortable: false,
            width: 20,
            center: true,
            selector: (row: { status_agent: string; }) => row.status_agent,
            cell: (row: any) => {
                let badgeContent;

                switch (row.status_agent) {
                    case 'online':
                        badgeContent = (
                            <span className="badge bg-soft-info-alternative text-info">
                                <i className="bi bi-check-circle-fill fs-6"></i>
                                <span className="fs-6 ms-1">{t('dashboard-operations.online')}</span>
                            </span>
                        );
                        break;
                    case 'offline':
                        badgeContent = (
                            <span className="badge bg-soft-danger text-danger">
                                <i className="bi bi-x-circle-fill fs-6"></i>
                                <span className="fs-6 ms-1">{t('dashboard-operations.offline')}</span>
                            </span>
                        );
                        break;
                    default:
                        badgeContent = (
                            <span className="badge bg-soft-success text-success">
                                <i className="bi bi-dash-circle-fill fs-6"></i>
                                <span className="fs-6 ms-1">{row.status_agent}</span>
                            </span>
                        );
                        break;
                }

                return <p className="text-nowrap mb-0">{badgeContent}</p>;
            }
        },
        {
            name: t('dashboard-operations.col-3'),
            sortable: false,
            width: 15,
            center: true,
            selector: (row: { time_since_login: string; }) => row.time_since_login,
            cell: (row: any) => <p className="text-nowrap mb-0">{row.time_since_login || <span className="badge bg-soft-danger text-danger">{t('dashboard-operations.empty')}</span>}</p>
        },
        {
            name: t('dashboard-operations.col-4'),
            sortable: false,
            width: 20,
            center: true,
            selector: (row: { conversations: string; }) => row.conversations,
            cell: (row: any) => <p className="text-nowrap mb-0">{NumberUtil.format(row.conversations) || <span className="badge bg-soft-danger text-danger">{t('dashboard-operations.empty')}</span>}</p>
        },
        {
            name: t("config.subaccount.actions"),
            sortable: false,
            center: true,
            cell: (row: any) => {
                if (row.id_perfil === 8) {
                    return (
                        <span className="text-muted">
                            <i className="bi bi-info-circle me-2 text-danger fw-bolder"></i>
                            {t("dashboard-operations.no-actions-available")}
                        </span>
                    );
                }

                return (
                    <div className="btn-group" role="group">
                        <button
                            className="btn bg-soft-info-alternative btn-sm"
                            type="button"
                            onClick={() => handleCloseSession(row.agent_id, row.username)}
                        >
                            <i className="bi bi-person-fill-dash fs-5"></i>
                            <span className="ms-2 text-white-dark">
                                {t("dashboard-operations.btn-actions-logout")}
                            </span>
                        </button>

                        <div className="btn-group">
                            <div
                                className="btn bg-soft-info-alternative btn-icon btn-sm dropdown-toggle dropdown-toggle-empty"
                                data-bs-toggle="dropdown"
                            />
                            <div className="dropdown-menu dropdown-menu-end mt-1">
                                <button
                                    className="dropdown-item text-white-dark"
                                    type="button"
                                    onClick={() => handleReleaseTickets(row.agent_id, row.username)}
                                >
                                    {t("dashboard-operations.btn-actions-release")}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            }
        }

    ];

    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const getConversations = async (filter: string) => {
        try {
            const response = await call(() => DashBoardService.getConversations(account.id, filter));
            const { tt_live, tt_close, sub_account } = response.data;

            sub_account.forEach(e => {
                if (!sub_accounts_map.current.get(e.id)) {
                    addOptionToFilter(e.name, e.id);
                    sub_accounts_map.current.set(e.id, e.name);
                }
            })
            if (shouldLockFilter && sub_account.length > 0) {
                const subId = String(sub_account[0].id);

                if (!lockedSubAccountRef.current) {
                    lockedSubAccountRef.current = subId;

                    selectedFilterRef.current = subId;
                    setSelectedFilterState(subId);
                }
            }

            setCardsConversations({
                today: tt_close.today,
                yesterday: tt_close.yesterday,
                total: tt_live.total,
                outAssign: tt_live.out_assing,
                outResponseAgent: tt_live.out_response_agent,
                outResponseClient: tt_live.out_response_client,
                slaMax: tt_live.sla_max,
                slaMin: tt_live.sla_min,
                slaAvg: tt_live.sla_avg
            });
        } catch (error) {
            console.error(t('dashboard-operations.error-1'), error);
            AlertService.toastError(t('dashboard-operations.error-5'))
        }
    };

    const getAgents = async (filter: string) => {
        try {
            const response = await call(() => DashBoardService.getAgentsMonitoring(account.id, filter));
            const { agents, chart } = response.data;
            const chartLabels = Object.keys(chart);
            const chartData = Object.values(chart);
            setCardsAgents({
                total: agents.total_agents,
                conected: agents.conected,
                break: agents.in_brake,
                asign: agents.whit_assignation,
                noasign: agents.out_assignattio,
                chart: {
                    labels: chartLabels,
                    data: chartData
                }
            });
        } catch (error) {
            console.error(t('dashboard-operations.error-2'), error);
            AlertService.toastError(t('dashboard-operations.error-5'))
        }
    };

    const getTipifications = async (filter: string) => {
        try {
            const response = await call(() => DashBoardService.getTipifications(account.id, filter));
            const data = response.data;
            setTipificationsToday({
                labels: Object.keys(data.tipifications_today),
                data: Object.values(data.tipifications_today)
            });
            setTipificationsMonth({
                labels: Object.keys(data.tipifications),
                data: Object.values(data.tipifications)
            });
            if (data.total_acumulated_today !== undefined && data.total_acumulated !== undefined) {
                setTotalAcumulatedToday(data.total_acumulated_today);
                setTotalAcumulatedMonth(data.total_acumulated);
            } else {
                setTotalAcumulatedToday(null);
                setTotalAcumulatedMonth(null);
            }
        } catch (error) {
            console.error(t('dashboard-operations.error-3'), error);
            AlertService.toastError(t('dashboard-operations.error-5'))
        }
    };

    const getAgentsDetail = async (filter: string) => {
        try {
            const { data } = await call(() => DashBoardService.getAgentsDetail(account.id, filter));
            setRows(data)
        } catch (error) {
            console.error(t('dashboard-operations.error-4'), error);
            AlertService.toastError(t('dashboard-operations.error-5'))
        }
    };

    const InfoCard = ({ icon, title, footerData, footerLabels, isMultiFooter = false, footerBgColor = 'bg-soft-success' }) => (
        <div className="col-sm-6 col-lg-2 mb-4">
            <div className="card card-transition card-hover-shadow text-center">
                <div className="card-body">
                    <div className="d-flex justify-content-center mb-2">
                        <i className={`bi ${icon} fs-1 text-primary`}></i>
                    </div>
                    <div className="mb-4">
                        <h5 className="mb-1">{title}</h5>
                    </div>
                </div>
                <div className={`card-footer ${footerBgColor}`}>
                    <div className={`row ${isMultiFooter ? 'col-divider' : ''}`}>
                        {footerData.map((data, index) => (
                            <div key={index} className="col">
                                <span className="h5">{data}</span>
                                <span className="d-block fs-5">{footerLabels[index]}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const handleTabActive = (tab: string) => {
        setActiveTab(tab);
    };

    const handleCloseSession = async (agent_id: number, username: string) => {
        await AlertService.warning(
            t('atention'),
            t('dashboard-operations.questions-1', { username: username }),
            async () => {
                try {
                    await call(() => DashBoardService.closeSessionAgent(agent_id));
                    AlertService.toastSuccess(t('dashboard-operations.success-1', { username: username }));
                } catch (error) {
                    console.error(t('dashboard-operations.error-1'), error);
                    AlertService.toastError(t('dashboard-operations.error-5'));
                }
            },
            t('dashboard-operations.btn-1')
        );
    };

    const handleReleaseTickets = async (agent_id: number, username: string) => {
        await AlertService.warning(
            t('atention'),
            t('dashboard-operations.questions-2', { username: username }),
            async () => {
                try {
                    await call(() => DashBoardService.releaseTicketsAgent(agent_id));
                    AlertService.toastSuccess(t('dashboard-operations.success-2', { username: username }));
                } catch (error) {
                    console.error(t('dashboard-operations.error-1'), error);
                    AlertService.toastError(t('dashboard-operations.error-5'));
                }
            },
            t('dashboard-operations.btn-2')
        );
    };

    const applyFilter = async () => {
        try {
            getConversations(selectedFilterRef.current);
            getTipifications(selectedFilterRef.current);
            getAgentsDetail(selectedFilterRef.current)
        } catch (error) {
            console.error(t('dashboard-operations.error-6'), error);
            AlertService.toastError(t('dashboard-operations.error-5'))
        }
    };

    const fetchData = async () => {
        try {
            const finalFilter = shouldLockFilter && lockedSubAccountRef.current
                ? lockedSubAccountRef.current
                : selectedFilterRef.current;

            await Promise.all([
                getConversations(finalFilter),
                getAgents(finalFilter),
                getTipifications(finalFilter),
                getAgentsDetail(finalFilter)
            ]);

            setLastUpdate(formatDate(new Date()));
            AlertService.toastSuccess(t('dashboard-operations.update-text'));

        } catch (error: any) {
            AlertService.toastError(t('dashboard-operations.error-5'), error);
        } finally {
            timeoutRef.current = setTimeout(fetchData, 10000);
        }
    };

    useEffect(() => {
        const init = async () => {
            if (shouldLockFilter) {
                await getConversations('general');
                fetchData();
            } else {
                fetchData();
            }
        };

        init();

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);


    return (
        <>
            <Header />
            <Aside />

            <main id="content" role="main" className="main">
                <div className="content container-fluid">
                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item">
                                            <span className="breadcrumb-link">{t('components.aside.operation-title')}</span>
                                        </li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                            {t('dashboard.dashboard-title')}
                                        </li>
                                    </ol>
                                </nav>
                                <h1 className="page-header-title text-white-dark">
                                    <i className="bi-speedometer2 nav-icon text-primary fw-bolder"></i> {t('dashboard.dashboard-title')}
                                </h1>
                            </div>
                            <div className="col-sm-auto">
                                <span className="badge bg-soft-primary text-primary rounded-pill fs-4 text-white-dark">{t('dashboard-operations.last-update')}: {lastUpdate} </span>
                            </div>
                        </div>
                    </div>
                    {/* Conversaions / tickets */}
                    <div className="row mb-3">
                        <div
                            className="alert mb-3"
                        >
                            <div className="row align-items-center">
                                <div className="col-auto">
                                    <img
                                        alt="conversations-tickets"
                                        className="avatar avatar-lg"
                                        src="../assets/svg/illustrations/agc.png"
                                    />
                                </div>
                                <div className="col">
                                    <h3 className="mb-1">
                                        {t('dashboard-operations.title-tickets')}
                                    </h3>
                                    <p className="mb-0">
                                        {t('dashboard-operations.subtitule-tickets')}
                                    </p>
                                </div>
                                <div className="col-auto">
                                    {!shouldLockFilter && (
                                        <div className="dropdown">
                                            <button
                                                type="button"
                                                className="btn btn-white btn-sm text-nowrap"
                                                id="usersFilterDropdown"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                            >
                                                <i className="bi-ui-radios me-1"></i> {t('dashboard-operations.filter-conversations')}
                                            </button>
                                            <div className="dropdown-menu dropdown-menu-end dropdown-card card-dropdown-filter-centered" aria-labelledby="usersFilterDropdown" style={{ minWidth: "20rem" }}>
                                                <div className="card">
                                                    <div className="card-header card-header-content-between">
                                                        <h5 className="card-header-title">{t("follow-up.sidebar.filter-dropdown-title")}</h5>
                                                        <button title="button" type="button" className="btn btn-ghost-secondary btn-icon btn-sm ms-2">
                                                            <i className="bi-x-lg"></i>
                                                        </button>
                                                    </div>
                                                    <div className="card-body">
                                                        <form>
                                                            <>
                                                                {filterOptions.map(option => (
                                                                    <div className="form-check mb-3" key={option.value}>
                                                                        <input
                                                                            type="radio"
                                                                            id={`formRadio-${option.value}`}
                                                                            className="form-check-input"
                                                                            checked={selectedFilterState === option.value}
                                                                            onChange={() => {
                                                                                selectedFilterRef.current = option.value;
                                                                                setSelectedFilterState(option.value);
                                                                            }}
                                                                            name="formRadio"
                                                                        />
                                                                        <div>
                                                                            <i className={`bi ${option.value === 'general' ? 'bi-chat-left-text' :
                                                                                option.value === 'agents' ? 'bi-person' :
                                                                                    option.value === 'bots' ? 'bi-robot nav-icon' :
                                                                                        'bi-people-fill'
                                                                                } me-2`}></i>
                                                                            <label className="form-check-label" htmlFor={`formRadio-${option.value}`}>
                                                                                {option.label}
                                                                            </label>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </>
                                                            <div className="d-grid">
                                                                <a className="btn btn-primary" onClick={applyFilter}>{t("follow-up.sidebar.filter-dropdown_aply")}</a>
                                                            </div>
                                                        </form>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                        <InfoCard
                            icon="bi-calendar-week-fill"
                            title={t('dashboard-operations.card-1')}
                            footerData={[NumberUtil.format(cardsConversations.today), NumberUtil.format(cardsConversations.yesterday)]}
                            footerLabels={[t('dashboard-operations.card-1'), t('dashboard-operations.ayer')]}
                            isMultiFooter={true}
                        />

                        <InfoCard
                            icon="bi-inbox-fill"
                            title={t('dashboard-operations.card-2')}
                            footerData={[NumberUtil.format(cardsConversations.total)]}
                            footerLabels={[t('dashboard-operations.total')]}
                        />

                        <InfoCard
                            icon="bi-clock-history"
                            title={t('dashboard-operations.card-3')}
                            footerData={[NumberUtil.format(cardsConversations.outAssign)]}
                            footerLabels={[t('dashboard-operations.total')]}
                        />

                        <InfoCard
                            icon="bi-person-fill-x"
                            title={t('dashboard-operations.card-4')}
                            footerData={[NumberUtil.format(cardsConversations.outResponseAgent)]}
                            footerLabels={[t('dashboard-operations.total')]}
                        />

                        <InfoCard
                            icon="bi-chat-dots-fill"
                            title={t('dashboard-operations.card-5')}
                            footerData={[NumberUtil.format(cardsConversations.outResponseClient)]}
                            footerLabels={[t('dashboard-operations.total')]}
                        />

                        <InfoCard
                            icon="bi-star-fill"
                            title={t('dashboard-operations.card-6')}
                            footerData={[cardsConversations.slaMax, cardsConversations.slaMin, cardsConversations.slaAvg]}
                            footerLabels={['Max', 'Min', 'AVG']}
                            isMultiFooter={true}
                        />
                    </div>
                    {/* Agents */}
                    <div className="row mb-3">
                        <div
                            className="alert mb-3"
                        >
                            <div className="d-sm-flex">
                                <div className="flex-shrink-0">
                                    <img
                                        alt="conversations-tickets"
                                        className="avatar avatar-lg mb-2 mb-sm-0"
                                        src="../assets/svg/illustrations/agent.png"
                                    />
                                </div>
                                <div className="flex-grow-1 ms-sm-3">
                                    <h3>
                                        {t('dashboard-operations.title-agents')}
                                    </h3>
                                    {t('dashboard-operations.subtitule-agents')}
                                </div>
                            </div>
                        </div>
                        <InfoCard
                            icon="bi-people-fill"
                            title={t('dashboard-operations.title-agents')}
                            footerData={[NumberUtil.format(cardsAgents.total)]}
                            footerLabels={[t('dashboard-operations.total')]}
                            footerBgColor="bg-soft-info-alternative"
                        />
                        <InfoCard
                            icon="bi-person-fill-check"
                            title={t('dashboard-operations.card-2-agents')}
                            footerData={[NumberUtil.format(cardsAgents.conected)]}
                            footerLabels={[t('dashboard-operations.total')]}
                            footerBgColor="bg-soft-info-alternative"
                        />
                        <InfoCard
                            icon="bi-person-fill-dash"
                            title={t('dashboard-operations.card-3-agents')}
                            footerData={[NumberUtil.format(cardsAgents.break)]}
                            footerLabels={[t('dashboard-operations.total')]}
                            footerBgColor="bg-soft-info-alternative"
                        />
                        <InfoCard
                            icon="bi-person-fill-up"
                            title={t('dashboard-operations.card-4-agents')}
                            footerData={[NumberUtil.format(cardsAgents.asign)]}
                            footerLabels={[t('dashboard-operations.total')]}
                            footerBgColor="bg-soft-info-alternative"
                        />
                        <InfoCard
                            icon="bi-person-fill-down"
                            title={t('dashboard-operations.card-5-agents')}
                            footerData={[NumberUtil.format(cardsAgents.noasign)]}
                            footerLabels={[t('dashboard-operations.total')]}
                            footerBgColor="bg-soft-info-alternative"
                        />

                        <div className="col-sm-6 col-lg-2 mb-4">
                            <div className="card card-transition card-hover-shadow text-center">
                                {cardsAgents.chart.data.length > 0 && cardsAgents.chart.data.some(dataValue => dataValue > 0) ? (
                                    <div className="card-body d-flex justify-content-center align-items-center flex-column">
                                        <h4 className="mb-4">{t('dashboard-operations.card-6-agents')}</h4>
                                        <PieChart
                                            datasetName='Total'
                                            width={200}
                                            height={131}
                                            legendDisplay={false}
                                            dataset={{
                                                labels: cardsAgents.chart.labels,
                                                data: cardsAgents.chart.data,
                                                backgroundColor: cardsAgents.chart.labels.map(l => ColorUtil.stringToRGB(l)),
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="card-body">
                                            <div className="d-flex justify-content-center mb-2">
                                                <i className="bi bi-pie-chart-fill fs-1 text-primary"></i>
                                            </div>
                                            <div className="mb-4">
                                                <h4 className="mb-1">{t('dashboard-operations.card-6-agents')}</h4>
                                            </div>
                                        </div>
                                        <div className="card-footer bg-soft-info-alternative">
                                            <div className="row col-divider">
                                                <div className="col">
                                                    <span className="d-block h5 mb-4">{t('dashboard-operations.empty')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <span className="divider-center mb-7">{t('dashboard-operations.separator')}</span>
                    {/* Resultados generales */}
                    <div className="row">
                        <div className="col-lg-4 mb-3 mb-lg-5">
                            <div className="card">
                                <div className="card-header card-header-content-between">
                                    <h4 className="card-header-title">
                                        {t('dashboard-operations.results')}
                                    </h4>
                                </div>
                                <div className="card-body text-center">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="text-start">
                                            <ul className="nav nav-segment nav-pills">
                                                <li className="nav-item cursor-pointer">
                                                    <a className={`nav-link ${activeTab === 'tabOne' ? 'active' : ''}`}
                                                        onClick={() => handleTabActive('tabOne')}>
                                                        <i className="bi bi-calendar-event-fill text-primary"></i>
                                                        {' '}{t('dashboard-operations.card-1')}
                                                    </a>
                                                </li>
                                                <li className="nav-item cursor-pointer">
                                                    <a className={`nav-link ${activeTab === 'tabTwo' ? 'active' : ''}`}
                                                        onClick={() => handleTabActive('tabTwo')}>
                                                        <i className="bi bi-calendar-week-fill text-primary"></i>
                                                        {' '}{t('dashboard-operations.mes')}
                                                    </a>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="h4 fw-bold">
                                            <span className="text-primary rounded-pill text-white-dark">
                                                {t('dashboard-operations.tipifications')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="text-center mb-0">
                                        {currentData.labels.length > 0 && currentData.data.length > 0 ? (
                                            <div className="d-flex flex-column align-items-center">
                                                <SemiDonutChart
                                                    datasetName="Resumen"
                                                    showLegend={false}
                                                    dataset={{
                                                        labels: currentData.labels,
                                                        data: currentData.data,
                                                        backgroundColor: currentData.labels.map(l => ColorUtil.stringToRGB(l)),
                                                    }}
                                                />
                                                {(totalAcumulatedToday !== null && totalAcumulatedMonth !== null) && (
                                                    <div className="mt-0">
                                                        <h3 className="text-primary">
                                                            {t('dashboard-operations.acumuled')}
                                                        </h3>
                                                        <div className="h1 text-primary">
                                                            ${NumberUtil.format(currentTotalAcumulated)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="alert my-5" role="alert">
                                                <div className="d-flex flex-column justify-content-center align-items-center">
                                                    <div className="flex-shrink-0">
                                                        <img
                                                            alt="Image Description"
                                                            className="avatar avatar-xl"
                                                            src="/assets/svg/illustrations/oc-browse.svg"
                                                            data-hs-theme-appearance="default"
                                                        />
                                                        <img
                                                            alt="Image Description"
                                                            className="avatar avatar-xl"
                                                            src="/assets/svg/illustrations/oc-browse-dark.svg"
                                                            data-hs-theme-appearance="dark"
                                                        />
                                                        <h3 className="alert-heading mb-1">
                                                            {t('dashboard-operations.empty')}
                                                        </h3>
                                                        <p className="mb-0">
                                                            {t('dashboard-operations.no-data')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-8 mb-3 mb-lg-5">
                            <CustomDataTable
                                columns={columns}
                                data={rows}
                                hideAccountsFilter
                                filter={(row, term) => {
                                    if (!term) return true;

                                    return (
                                        `${row.username}`.match(new RegExp(`${term}`, 'i')) ||
                                        `${row.status_agent}`.match(new RegExp(`${term}`, 'i')) ||
                                        `${row.time_since_login}`.match(new RegExp(`${term}`, 'i')) ||
                                        `${row.conversations}`.match(new RegExp(`${term}`, 'i'))
                                    )
                                }}
                            />
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
        </>
    );
}
